import { useToast } from "@/hooks/use-toast";
import { Info } from "lucide-react";
import "./custom-toast.scss";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";

export interface CustomShowToastProps {
  title: string;
  description?: string;
  action?: React.ReactNode;
  icon?: React.ReactNode;
  progress?: boolean;
  position?:
    | "top-right"
    | "top-left"
    | "top-center"
    | "bottom-right"
    | "bottom-left";
  width?: string;
  height?: string;
  delay?: number;
  permanent?: boolean;
  customPosition?: string;
  defaultIcon?: React.ReactNode;
}

export default function CustomToast() {
  const { toast, dismiss } = useToast();

  const sharedClasses = "fixed ";

  const positionReturn = (
    position?:
      | "top-right"
      | "top-left"
      | "top-center"
      | "bottom-right"
      | "bottom-left"
  ) => {
    switch (position) {
      case "top-right":
        return "top-5 right-5";
      case "top-left":
        return "top-5 left-5";
      case "top-center":
        return "top-5 left-1/2 -translate-x-1/2";
      case "bottom-right":
        return "bottom-5 right-5";
      case "bottom-left":
        return "bottom-5 left-5";
      default:
        return "";
    }
  };

  const ShowToast = ({
    title,
    description,
    customPosition,
    action,
    defaultIcon,
    icon,
    progress = true,
    position = !customPosition ? "bottom-right" : undefined,
    width = "max-w-max",
    height = "max-h-max",
    delay = 5000,
    permanent = false,
  }: CustomShowToastProps) => {
    const toastInstance = toast({
      className: `${sharedClasses} ${positionReturn(
        position
      )} ${width} ${height} ${customPosition || ""} `,
      // @ts-ignore
      title: (
        <div className="flex flex-col gap-[4px]">
          <div className="flex items-center gap-2 font-600 text-sm leading-5">
            {icon ? icon : defaultIcon}
            <span dangerouslySetInnerHTML={{ __html: title }}></span>
          </div>
          {description && (
            <div className="flex items-center gap-2 font-600 text-sm leading-5">
              {icon ? (
                icon
              ) : (
                <DotLottieReact
                  src="/animations/lottie/success-animation.json"
                  style={{
                    width: "24px",
                    height: "24px",
                    visibility: "hidden",
                  }}
                  autoplay={true}
                />
              )}
              <p className="font-500 text-sm max-w-[350px] break-words">
                {description}
              </p>
            </div>
          )}
        </div>
      ),
      // description: description,
      style: {},
      action: (
        <div className="max-w-max flex items-center pr-4">
          {action}
          {progress && (
            <div className="progress-bar-container bg-red-500 w-full !h-1">
              <div className="progress-bar animate-progress w-full !h-1 absolute left-0 bottom-0"></div>
            </div>
          )}
        </div>
      ),
      duration: permanent ? Infinity : delay,
    });
    return toastInstance;
  };

  const ShowWithlessIcon = (props: CustomShowToastProps) => {
    const toastInstance = ShowToast({
      ...props,
      defaultIcon: null,
    });
    return toastInstance;
  };

  const success = (props: CustomShowToastProps) => {
    const toastInstance = ShowToast({
      ...props,
      defaultIcon: (
        <DotLottieReact
          src="/animations/lottie/success-animation.json"
          style={{
            width: "24px",
            height: "24px",
          }}
          autoplay={true}
        />
      ),
    });
    return toastInstance;
  };

  const error = (props: CustomShowToastProps) => {
    const toastInstance = ShowToast({
      ...props,
      defaultIcon: (
        <DotLottieReact
          src="/animations/lottie/error.lottie"
          style={{
            width: "24px",
            height: "24px",
          }}
          autoplay={true}
        />
      ),
    });
    return toastInstance;
  };

  const warning = (props: CustomShowToastProps) => {
    const toastInstance = ShowToast({
      ...props,
      defaultIcon: (
        <DotLottieReact
          src="/animations/lottie/warning.lottie"
          style={{
            width: "24px",
            height: "24px",
          }}
          autoplay={true}
        />
      ),
    });
    return toastInstance;
  };

  const info = (props: CustomShowToastProps) => {
    const toastInstance = ShowToast({
      ...props,
      defaultIcon: <Info className="text-blue-500 w-[24px] h-[24px]" />,
    });
    return toastInstance;
  };

  const removeAllToast = () => {
    dismiss();
  };

  return { success, error, warning, info, ShowWithlessIcon, removeAllToast };
}
